/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * GH-parity autoplay continuation: when the player runs to the end of a
 * cloud queue with nothing left to play and the user's Autoplay setting
 * is on, re-seed a fresh cloud queue off the finished one so playback
 * keeps going with machine-picked similar tracks.
 *
 * Mirrors hornet's `PlaybackController._handlePlayerQueueEnded`, which
 * gates on `queueStore.autoplayEnabled && onLastRemoteQueueItem` and
 * starts a new queue seeded on the finished queue's MRN with
 * `contexts: { AUTOPLAY: true }`. Our end-of-queue signal is a
 * `skipNext()` that resolves null (fell off the tail) — the single
 * decision point lives in `playerPlaylistBinding`'s `trackEnded` handler.
 */

import { createLogPrefix, logVerbose, type LogTarget } from '@amazon/vinyl-util'
import type { PlaylistController } from '@amzn/amp-playback'
import type { CloudQueueController } from '@/cloudQueue/CloudQueueController'
import { isAutoplayEnabled } from '@/util/autoplay'

const logTarget: LogTarget = { logPrefix: createLogPrefix('autoplayContinuation') }

export interface AutoplayContinuationDeps {
    readonly playlistController: PlaylistController
    readonly cloudQueueController: CloudQueueController
}

/**
 * Attempt to extend the just-ended queue with an autoplay continuation.
 * Returns true when a continuation queue was created and set. No-ops
 * (returns false) when autoplay is off, when there's no live cloud queue
 * to seed from (e.g. a video / podcast feed sets `cloudQueueOptions:
 * null`), or when the gateway can't build a continuation.
 */
export async function maybeStartAutoplayContinuation(
    deps: AutoplayContinuationDeps
): Promise<boolean> {
    if (!isAutoplayEnabled()) return false
    const queueId = deps.playlistController.cloudQueueOptions.value?.queueId
    if (!queueId) return false
    const { createAutoplayContinuationQueue, continuationQueuePlayParams } =
        await import('@/cloudQueue/cloudQueueService')
    const queueResult = await createAutoplayContinuationQueue(queueId)
    // A gateway that returns no continuation window (empty queue) leaves
    // playback stopped rather than swapping in an empty feed.
    if (!queueResult.createQueue?.queue?.id) return false
    logVerbose(
        logTarget,
        `[preload-trace] autoplayContinuation: seed queueId=${queueId} -> setQueue`
    )
    deps.cloudQueueController.setQueue({
        playParams: continuationQueuePlayParams(queueId),
        queueResult,
        autoContinuation: true,
    })
    return true
}
